import { openModal } from "action/modal/openModal";
import { ICompactProduct } from "api/interfaces/get-page-details/common/CompactProduct.type";
import {
  IProductReview,
  IReviewsInfo,
} from "api/interfaces/product-review.type";
import { getReviews } from "api/product-reviews.api";
import StarRatings from "component/design-system/StarRatings";
import PAButton, {
  BUTTON_TYPE,
} from "component/design-system/buttons/PAButton";
import Slider from "component/design-system/carousel/Slider";
import HorizontalSeparator from "component/design-system/horizontal-separator/HorizontalSeparator";
import PAImage from "component/design-system/images/PAImage";
import PAText, {
  FONT_SIZE,
} from "component/design-system/typography/PAText/PAText";
import ProgressiveBar from "component/progressiveBar/ProgressiveBar";
import useScreenSize from "hoc/useScreenSize";
import { RootState } from "interfaces/generics.type";
import { useTranslation } from "next-i18next";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import HideIf from "util/HideIf";
import { getRelativeImagePath } from "util/helper/getRelativeImagePath";
import SingleReviewCard from "./SingleReviewCard";
import CreateReviewModal from "./modal/CreateReviewModal";
import MobileReviewsModal from "./modal/MobileSeeAllReviewsModal";
import styles from "./productReviews.module.scss";
import { initialReviewsInfo } from "./reviewUtil";

interface IProps {
  product: ICompactProduct;
}

const ProductReviews = ({ product }: IProps) => {
  const [sortBy, setSortBy] = useState("mostRecent");
  const [currentPage, setCurrentPage] = useState(0);
  const [mobilePage, setMobilePage] = useState(0);
  const [showCreateReviewModal, setShowCreateReviewModal] = useState(false);
  const [showAllMobileReviewsModal, setShowAllMobileReviewsModal] = useState(false);
  const [reviewsInfo, setReviewsInfo] = useState<IReviewsInfo>(initialReviewsInfo);
  const [mobileReviews, setMobileReviews] = useState<IProductReview[]>([]);
  const [redirected, setRedirected] = useState(false);
  const [prefilledReview, setPrefilledReview] = useState();

  const {
    reviews,
    ratingSnapShot,
    totalPages,
    totalReviewsCount,
    averageRating,
  } = reviewsInfo;

  const pageSize = 8;

  const [isMobile, isTablet] = useScreenSize();

  const { loggedIn } = useSelector(
    (state: RootState) => state.authenticationState,
  );
  const dispatch = useDispatch();

  const reviewContainerRef = useRef(null);
  const { t } = useTranslation("product");

  const getProductReviews = async (page: number, sortByValue: string) => {
    const res = await getReviews(product?.sku, page, pageSize, sortByValue);
    if (page > 0 && res.data.reviews.length === 0) {
      return;
    }
    setReviewsInfo(page > 0 ? { ...reviewsInfo, reviews: [...reviewsInfo.reviews, ...res.data.reviews] } : res?.data || initialReviewsInfo);
    setMobileReviews([...mobileReviews, ...(res?.data?.reviews || [])]);
  };

  function postDeleteReview() {
    getProductReviews(0, sortBy);
  }

  const handleSorting = (value: string) => {
    getProductReviews(0, value);
    setSortBy(value);
    setCurrentPage(0);
    setMobilePage(0);
  };

  useEffect(() => {
    if (isMobile) {
      getProductReviews(mobilePage, sortBy);
    }
  }, [mobilePage, loggedIn]);

  useEffect(() => {
    if (!isMobile) {
      getProductReviews(currentPage, sortBy);
    }
  }, [currentPage, loggedIn]);

  const reviewProductClick = (review?: IProductReview) => {
    if (loggedIn) {
      setShowCreateReviewModal(true);
      if (review) {
        setPrefilledReview(review);
      }
    } else {
      setRedirected(true);
      dispatch(
        openModal("login", {
          loginMessage: "Please Login/Sign up to Review this Product",
        }),
      );
    }
  };

  function handleOpenCustomerImages(idx: number) {
    dispatch(openModal("thumbnailImage", {
      images: reviewsInfo.allImages,
      imageIndex: idx,
      title: "Customer Photos",
    }));
  }

  useEffect(() => {
    if (loggedIn && redirected) {
      setShowCreateReviewModal(true);
      setRedirected(false);
    }
  }, [loggedIn, redirected]);

  const displayReviews = isMobile ? reviews.slice(0, 2) : reviews;

  return (
    <div>
      <HideIf show={!isMobile}>
        <PAText bold component="h3" className="m-t-20 m-b-20">
          {t("Ratings & Reviews")}
        </PAText>
      </HideIf>

      <div
        className={`${styles.review_product_container} ${isMobile ? styles.mobile_review_product_container : ""
          }`}
        id="review_product_container"
      >
        <div className={styles.review_this_product}>
          <PAText fontSize={FONT_SIZE[3]} bold inlineBlock className="m-b-5" color="black">
            {totalReviewsCount ? t("Real Customers, Real Reviews") : t("Review this Product")}
          </PAText>

          <PAText color="grey" component="p" className={styles.review_product_text}>
            {t("Share your thoughts with other customers")}
          </PAText>

          <PAButton
            buttonType={BUTTON_TYPE.NORMAL}
            className={styles.leave_review_button}
            onClick={() => reviewProductClick()}
            bold
          >
            {t("Leave a review")}
          </PAButton>

          <PAButton
            buttonType={BUTTON_TYPE.PRIMARY}
            className={styles.review_button}
            onClick={() => reviewProductClick()}
          >
            {t("Review this Product")}
          </PAButton>
        </div>

        {totalReviewsCount ? (
          <>
            <div className={styles.star_container}>
              <PAText inlineBlock className={styles.average_rating} color="black" bold>
                {averageRating.toFixed(1)}
              </PAText>
              <StarRatings rating={averageRating} filled className="fa-2x" />
              <PAText color="grey">
                {`${totalReviewsCount} ${t(totalReviewsCount > 1 ? t("reviews") : t("review"))}`}
              </PAText>
            </div>

            <HideIf show={isMobile}>
              <HorizontalSeparator className="m-t-10 m-b-10" />
            </HideIf>

            <div className={styles.rating_snapshot}>
              <PAText fontSize={FONT_SIZE[2]} className="m-t-5" bold color="black">
                {t("Rating Snapshot")}
              </PAText>
              {[...ratingSnapShot].reverse().map(({ count }, i) => (
                <div key={i} className={styles.progressive_bar_container}>
                  <i className="fa fa-star m-r-5" />
                  <PAText className="m-r-20" fontSize={FONT_SIZE[2]}>
                    {5 - i}
                  </PAText>
                  <ProgressiveBar
                    current={count}
                    total={totalReviewsCount}
                    className={styles.progressive_bar}
                  />
                  <PAText color="grey">{count.toFixed()}</PAText>
                </div>
              ))}
            </div>
            <HideIf show={isMobile}>
              <HorizontalSeparator className="m-t-10 m-b-10" />
            </HideIf>
          </>
        ) : (
          <>
            <div />
            <div className={styles.star_container}>
              <PAText inlineBlock color="alert-secondary">
                {Array.from({ length: 5 }, (_, index) => (
                  <i className={`fa fa-2x fa-star-o ${styles.star}`} key={index} />
                ))}
              </PAText>
              <PAText color="grey" className="m-b-20">{t("No reviews have been submitted yet.")}</PAText>
            </div>
          </>
        )}
      </div>

      <HideIf show={reviewsInfo.allImages.length > 0}>
        <div className="p-l-10 p-r-10">
          <PAText
            component="h4"
            className="m-t-0 m-b-0"
            inlineBlock
            bold
          >
            {t("Customer Photos")}
          </PAText>
          <Slider tileWidth={150} ulClass={styles.product_review_slider_ul}>
            {reviewsInfo.allImages.map((photo, idx) => (
              <div
                key={photo}
                onClick={() => handleOpenCustomerImages(idx)}
              // style={{ width: "100%" }}
              >
                <PAImage
                  width={150}
                  height={150}
                  src={getRelativeImagePath(photo)}
                  alt={photo}
                />
              </div>
            ))}
          </Slider>
          <HorizontalSeparator className="m-t-10 m-b-10" />
        </div>
      </HideIf>

      <HideIf show={!isMobile}>
        <div
          className={`d-flex justify-content-between m-t-40 ${styles.sorting}`}
          ref={reviewContainerRef}
        >
          {totalPages > 1 && !isMobile ? (
            <PAText fontSize={FONT_SIZE[2]}>
              {t("review_pagination", {
                count: Math.min(
                  (currentPage + 1) * pageSize,
                  totalReviewsCount,
                ),
                totalCount: totalReviewsCount,
              })}
            </PAText>
          ) : (
            <div />
          )}
          <HideIf show={totalReviewsCount > 1}>
            <PAText fontSize={FONT_SIZE[2]}>
              {t("Sort By: ")}
              <select
                value={sortBy}
                onChange={(e) => handleSorting(e.target.value, reviewsInfo)}
                className={`${styles.select} ${isTablet ? styles.tablet_select : ""
                  }`}
              >
                <option value="mostRecent">{t("Most Recent")}</option>
                <option value="highToLow">{t("Highest to Lowest")}</option>
                <option value="lowToHigh">{t("Lowest to Highest")}</option>
              </select>
            </PAText>
          </HideIf>
        </div>
      </HideIf>

      {displayReviews.map((review: IProductReview, index) => (
        <>
          <SingleReviewCard key={review?.reviewId} review={review} handleEditReview={() => reviewProductClick(review)} postDeleteReview={postDeleteReview} />
          {index !== displayReviews.length - 1 && <HorizontalSeparator className="m-t-0 m-b-0" />}
        </>
      ))}

      <HideIf show={!isMobile && reviewsInfo.reviews.length > 0 && currentPage + 1 < totalPages}>
        <div className="d-flex justify-content-center m-b-20">
          <PAButton
            buttonType={BUTTON_TYPE.OUTLINE}
            widthAuto
            onClick={() => setCurrentPage(currentPage + 1)}
          >
            {t("See More Reviews")}
          </PAButton>
        </div>
      </HideIf>
      <HideIf show={showCreateReviewModal}>
        <CreateReviewModal
          product={product}
          prefilledReview={prefilledReview}
          onHide={() => {
            setShowCreateReviewModal(false);
            setPrefilledReview(null);
          }}
        />
      </HideIf>

      <HideIf show={showAllMobileReviewsModal}>
        <MobileReviewsModal
          product={product}
          reviews={mobileReviews}
          totalPages={totalPages}
          totalReviewsCount={totalReviewsCount}
          averageRating={averageRating}
          ratingSnapShot={ratingSnapShot}
          setMobilePage={setMobilePage}
          mobilePage={mobilePage}
          onHide={() => {
            setShowAllMobileReviewsModal(false);
            setPrefilledReview(null);
          }}
          handleEditReview={reviewProductClick}
          postDeleteReview={postDeleteReview}
        />
      </HideIf>

      <HideIf show={isMobile && totalReviewsCount > 2}>
        <div
          className={styles.see_more_reviews}
          onClick={() => setShowAllMobileReviewsModal(true)}
        >
          <PAButton
            buttonType={BUTTON_TYPE.OUTLINE}
            onClick={() => setCurrentPage(currentPage + 1)}
            bold
          >
            {t("View All Reviews")}
          </PAButton>
        </div>
      </HideIf>

      <HorizontalSeparator className="m-b-20 m-t-30" />

    </div>
  );
};

export default ProductReviews;
